import { FC, useEffect } from 'react';
import { MdCheck, MdOutlineContentCopy } from 'react-icons/md';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Heading,
  Icon,
  Input,
  Link,
  Skeleton,
  Stack,
  Tag,
  Text,
  useClipboard,
} from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  AuthenticatorApp,
  GoogleAuthApp,
  IconEmail,
  LastPassAuthApp,
  MicrosoftAuthApp,
} from '../assets';
import { useAuthenticatorModal } from '../hooks';
import { TwoFactorQrCode } from './TwoFactorQrCode';

type AuthenticatorAppModalProps = {
  modalApi: UseOpenCloseReturn;
  onDone?: () => void;
  isClosable?: boolean;
  successPath: string;
  successIcon: any;
};
export const AuthenticatorAppModal: FC<AuthenticatorAppModalProps> = ({
  modalApi,
  onDone,
  isClosable,
  successPath,
  successIcon,
}) => {
  const {
    provisioningUri,
    qrCode,
    authCode,
    twoFactorMethodModal,
    scanCode,
    enterCode,
    successMsg,
    errorCode,
    twoFactorMethod,
    setTwoFactorMethod,
    setQrCode,
    showScanCode,
    showEnterCode,
    verifyQrCode,
    closeAndSetAuth,
    showChooseTwoFactorMethod,
  } = useAuthenticatorModal(isClosable, successPath, onDone, modalApi.onClose);

  const { copy, copied } = useClipboard({ value: authCode });

  let setupTwoFactorText;
  let enterCodeText;
  let successMessageText;

  switch (twoFactorMethod) {
    case 'app':
      setupTwoFactorText = 'Set up authenticator app';
      enterCodeText = 'Set up authenticator app';
      successMessageText =
        'Next time you log in you’ll need use your authenticator to verify your identity.';
      break;
    case 'sms':
      setupTwoFactorText = 'Set up text message verification';
      enterCodeText = 'Set up text message verification';
      successMessageText =
        'Next time you log we’ll send you a text message to verify your identity.';
      break;
    case 'mail':
      setupTwoFactorText = 'Enter the 6-digit code you see in the mail.';
      enterCodeText = 'Enter the 6-digit code you see in the mail.';
      successMessageText = '';
      break;
    default:
  }

  useEffect(() => {
    showChooseTwoFactorMethod();
  }, [modalApi.open]);

  const hoverProperties = { borderColor: 'primary.400', cursor: 'pointer' };

  return (
    <Dialog.Root
      closeOnEscape={isClosable}
      closeOnInteractOutside={isClosable}
      onOpenChange={modalApi.onOpenChange}
      open={modalApi.open}
      placement="center"
      size={!successMsg ? 'lg' : 'sm'}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content borderRadius="0">
          {twoFactorMethodModal ? (
            <Dialog.Header display="flex">
              <Dialog.Title fontWeight="normal" textStyle="sm">
                Set up 2-step verification
              </Dialog.Title>
            </Dialog.Header>
          ) : (
            <Dialog.Header display={!successMsg ? 'flex' : 'none'}>
              <Dialog.Title fontWeight="normal" textStyle="sm">
                {setupTwoFactorText}
              </Dialog.Title>
            </Dialog.Header>
          )}

          <Text
            color="gray.700"
            display={scanCode ? 'flex' : 'none'}
            justifyContent="center"
            ml={6}
            mt="4"
            position="relative"
            textAlign="left"
          >
            Scan this QR code with your authenticator app.
          </Text>

          <Text
            color="gray.700"
            display={enterCode ? 'flex' : 'none'}
            ml={6}
            position="relative"
            textAlign="left"
            top="-7px;"
          >
            {enterCodeText}
          </Text>
          {isClosable && (
            <Dialog.CloseTrigger asChild>
              <CloseButton />
            </Dialog.CloseTrigger>
          )}
          <Dialog.Body>
            {/* Choose 2fa method step */}
            <Flex
              display={twoFactorMethodModal ? 'block' : 'none'}
              justifyContent="center"
            >
              <Flex direction="row">
                <Flex
                  _hover={hoverProperties}
                  alignItems="center"
                  aria-label="Email"
                  as="button"
                  border="1px"
                  borderColor="gray.200"
                  direction="column"
                  justifyContent="start"
                  ml={5}
                  mr={2}
                  onClick={() => {
                    setTwoFactorMethod('mail');
                    showEnterCode();
                  }}
                  p={7}
                  textAlign="start"
                  width="300px"
                >
                  <Icon
                    as={IconEmail}
                    boxSize={20}
                    mb="20px"
                    mt="20px"
                    mx="20px"
                  />
                  <Text fontWeight="bold">Email</Text>
                  <Text textAlign="center">
                    We’ll send you a code each time you login.
                  </Text>
                </Flex>

                <Flex
                  _hover={hoverProperties}
                  alignItems="center"
                  aria-label="Authenticator app"
                  as="button"
                  border="1px"
                  borderColor="gray.200"
                  direction="column"
                  justifyContent="start"
                  ml={2}
                  mr={5}
                  onClick={() => {
                    setTwoFactorMethod('app');
                    showScanCode();
                  }}
                  p={7}
                  textAlign="start"
                  width="300px"
                >
                  <Icon
                    as={AuthenticatorApp}
                    boxSize={20}
                    mb="20px"
                    mt="20px"
                    mx="20px"
                  />
                  <Text fontWeight="bold">Authenticator app</Text>
                  <Text textAlign="center">
                    Get verification codes from an authenticator app.
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {/* Scan code step */}
            <Box display={scanCode ? 'block' : 'none'}>
              <Flex
                alignItems="center"
                direction="column"
                justifyContent="center"
              >
                <Box mt={1}>
                  <TwoFactorQrCode
                    provisioningUri={provisioningUri}
                    size={296}
                  />
                </Box>
                {authCode ? (
                  <Stack mb="8" textAlign="center">
                    <Text color="gray.700" fontWeight="400">
                      OR
                    </Text>
                    <Text color="gray.700" fontWeight="400">
                      Use this authenticator key in your app.
                    </Text>
                    <Tag.Root
                      aria-label="Copy authenticator key"
                      as="button"
                      cursor="pointer"
                      onClick={() => {
                        copy();
                      }}
                      w="100%"
                    >
                      <Tag.Label w="100%">{authCode}</Tag.Label>
                      <Tag.EndElement>
                        {copied ? <MdCheck /> : <MdOutlineContentCopy />}
                      </Tag.EndElement>
                    </Tag.Root>
                  </Stack>
                ) : (
                  <Skeleton height="50px" my={2} width={150} />
                )}
                <Text color="gray.700" fontWeight="400" mb={2}>
                  Supported authenticator apps
                </Text>
              </Flex>

              <Flex alignItems="center" direction="row" justifyContent="center">
                <Icon as={MicrosoftAuthApp} boxSize="32px" mr={2} />
                <Icon as={GoogleAuthApp} boxSize="32px" mr={2} />
                <Icon as={LastPassAuthApp} boxSize="32px" />
              </Flex>
            </Box>

            {/* Enter code step */}
            <Box display={enterCode ? 'flex' : 'none'}>
              <Field.Root mt={4} px={6}>
                <Field.Label>
                  Enter code
                  <Field.RequiredIndicator color="primary.600" />
                </Field.Label>
                <Input
                  borderColor="gray.300"
                  borderRadius="6"
                  onChange={(event) => setQrCode(event.target.value)}
                  placeholder="Enter your code"
                  size="lg"
                  type="text"
                  value={qrCode}
                  variant="outline"
                />
                {errorCode ? (
                  <Text color="red" role="alert">
                    {errorCode}
                  </Text>
                ) : null}
                {twoFactorMethod === 'mail' ? (
                  <Flex mt={1}>
                    <Text mr={1}>Didn&apos;t receive a code?</Text>
                    <Link
                      as="button"
                      onClick={() => showEnterCode(true)}
                      type="button"
                    >
                      Send new code
                    </Link>
                  </Flex>
                ) : null}
              </Field.Root>
            </Box>

            {/* Success message step */}
            <Box display={successMsg ? 'block' : 'none'}>
              <Flex alignItems="center" direction="column">
                <Icon
                  alignItems="center"
                  as={successIcon}
                  boxSize="132px"
                  mb={6}
                  mt={20}
                />
                <Heading as="h3" color="gray.700" fontSize="18px">
                  2-Step verification setup complete
                </Heading>
                <Text
                  color="gray.700"
                  fontWeight="400"
                  mt={2}
                  textAlign="center"
                >
                  {successMessageText}
                </Text>
              </Flex>
            </Box>
          </Dialog.Body>

          <Dialog.Footer>
            {/* Choose 2fa method */}
            <Button
              border="1px solid"
              borderColor="gray.300"
              color="gray.700"
              disabled
              display={twoFactorMethodModal ? 'flex' : 'none'}
              mr={4}
              variant="outline"
            >
              Back
            </Button>
            <Button
              colorPalette="primary"
              disabled
              display={twoFactorMethodModal ? 'flex' : 'none'}
              variant="solid"
            >
              Next
            </Button>
            {/* Choose 2fa method End */}

            {/* Show Scan code */}
            <Button
              border="1px solid"
              borderColor="gray.300"
              color="gray.700"
              display={scanCode ? 'flex' : 'none'}
              mr={4}
              onClick={showChooseTwoFactorMethod}
              variant="outline"
            >
              Back
            </Button>
            <Button
              colorPalette="primary"
              display={scanCode ? 'flex' : 'none'}
              onClick={() => showEnterCode()}
              variant="solid"
            >
              Next
            </Button>
            {/* Show Qr code End */}

            {/* Enter code */}
            <Button
              border="1px solid"
              borderColor="gray.300"
              color="gray.700"
              display={enterCode ? 'flex' : 'none'}
              mr={4}
              onClick={() =>
                twoFactorMethod === 'app'
                  ? showScanCode()
                  : showChooseTwoFactorMethod()
              }
              variant="outline"
            >
              Back
            </Button>
            <Button
              colorPalette="primary"
              display={enterCode ? 'flex' : 'none'}
              onClick={verifyQrCode}
              variant="solid"
            >
              Verify
            </Button>
            {/* Enter code End */}

            {/* Success  message */}
            <Button
              colorPalette="primary"
              display={successMsg ? 'flex' : 'none'}
              onClick={closeAndSetAuth}
              variant="solid"
              w="full"
            >
              Close
            </Button>
            {/* Success  message End */}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
